import { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { AppShell, Crumb } from '@agribot/polygon-checks-ui'
import { useCapabilities, useSession } from '@agribot/polygon-checks-ui'
import { useUsage } from './hooks'
import { Billing } from './Billing'
import { EmptyState, Eyebrow, Mono, Surface } from '@agribot/polygon-checks-ui'
import { Skeleton, SkeletonGridRows } from '@agribot/polygon-checks-ui'
import { Segmented } from '@agribot/polygon-checks-ui'
import type { UsageRow } from './types'
import { formatDate, ha, num } from '@agribot/polygon-checks-ui'
import { tokens } from '@agribot/polygon-checks-ui'

type Group = 'organisation' | 'user' | 'key' | 'day' | 'kind'

const GROUPS: { value: Group; label: string; hint: string; platformOnly?: boolean }[] = [
  {
    value: 'organisation', label: 'Organisation', platformOnly: true,
    hint: 'Who is using the platform',
  },
  { value: 'user', label: 'User', hint: 'What a per-user rate limit would read' },
  { value: 'key', label: 'Key', hint: 'The root key that provisioned each user' },
  { value: 'day', label: 'Day', hint: 'Over time' },
  { value: 'kind', label: 'Activity', hint: 'Ingest against validation' },
]

const PERIODS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
]

const COLUMNS = [
  { key: 'group', label: '', width: 'minmax(220px,1.4fr)' },
  { key: 'events', label: 'Events', width: '90px', align: 'right' as const },
  { key: 'uploads', label: 'Uploads', width: '90px', align: 'right' as const },
  { key: 'runs', label: 'Runs', width: '90px', align: 'right' as const },
  { key: 'plots', label: 'Polygons', width: '110px', align: 'right' as const },
  { key: 'area_ha', label: 'Area', width: '120px', align: 'right' as const },
  { key: 'last_seen', label: 'Last seen', width: '130px', align: 'right' as const },
]
const GRID = COLUMNS.map((c) => c.width).join(' ')

/**
 * What has been used.
 *
 * Read from the append-only ledger rather than from the live tables: a run's
 * summary is rewritten whenever a plot is repaired, and deleting a dataset
 * takes its runs with it, so neither could be billed against honestly.
 *
 * A platform operator can widen the scope past their own organisation. That
 * is the only difference between the two views, so it is one screen.
 */
export function Usage() {
  const { data: me } = useSession()
  const platform = useCapabilities().platform

  const [params, setParams] = useSearchParams()
  const scope = platform && params.get('scope') !== 'organisation' ? 'platform' : 'organisation'
  const days = Number(params.get('days') ?? 30) || 30
  // Platform admins open on what subscriptions bill against; everyone else sees the ledger.
  const view = platform && params.get('view') !== 'activity' ? 'billing' : 'activity'

  // 'organisation' is meaningless within one organisation, so a bookmarked
  // platform URL opened by an ordinary operator falls back rather than 400s.
  const requested = (params.get('group') as Group) ?? (scope === 'platform' ? 'organisation' : 'user')
  const group: Group = requested === 'organisation' && scope !== 'platform' ? 'user' : requested

  /**
   * Rounded down to the minute, and memoised.
   *
   * A window computed straight from `Date.now()` is a different string on
   * every render, so it is a different query key on every render: the query
   * refetches, that re-renders, and round it goes. On an organisation with
   * usage the loop hides behind cached rows; on an empty one it is a
   * spinner that never stops.
   */
  const since = useMemo(() => {
    const from = new Date(Date.now() - days * 86_400_000)
    from.setSeconds(0, 0)
    return from.toISOString()
  }, [days])

  const { data, isLoading } = useUsage({ group, from: since }, scope)

  const set = (key: string, value: string) =>
    setParams((p) => {
      p.set(key, value)
      return p
    }, { replace: true })

  const totals = data?.totals
  const rows = data?.rows ?? []
  const groups = GROUPS.filter((g) => !g.platformOnly || scope === 'platform')

  return (
    <AppShell crumbs={[<Crumb key="a">Admin</Crumb>, <Crumb key="b">Usage</Crumb>]}>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }}>
        <Eyebrow sx={{ mb: 2 }}>Admin</Eyebrow>
        <Typography variant="h1" sx={{ mb: 1 }}>Usage</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5, maxWidth: 680 }}>
          {view === 'billing'
            ? 'What each subscription bills against: suppliers in scope of open campaigns, unique hectares verified and seats, against the allowances set for the organisation.'
            : 'Recorded when plots are ingested and when a run completes. A run that failed is not counted. Polygons and area are summed per event, so a file that was ingested and then validated appears in both.'}
        </Typography>

        {platform && (
          <Box sx={{ mb: 5, display: 'flex' }}>
            <Segmented
              ariaLabel="View"
              value={view}
              onChange={(v) => set('view', v)}
              options={[
                { value: 'billing', label: 'Subscription' },
                { value: 'activity', label: 'Activity breakdown' },
              ]}
            />
          </Box>
        )}

        {view === 'billing' ? <Billing /> : (<>
        {/* --- Scope, period and grouping --- */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 5, flexWrap: 'wrap' }}>
          {platform && (
            <Segmented
              ariaLabel="Scope"
              value={scope}
              onChange={(v) => set('scope', v)}
              options={[
                { value: 'platform', label: 'All organisations' },
                { value: 'organisation', label: me?.organisation_name ?? 'Mine' },
              ]}
            />
          )}
          <Segmented
            options={PERIODS.map((p) => ({ value: String(p.days), label: p.label }))}
            value={String(days)}
            onChange={(v) => set('days', v)}
            ariaLabel="Period"
          />
          <Segmented
            options={groups.map((g) => ({ value: g.value, label: g.label }))}
            value={group}
            onChange={(v) => set('group', v)}
            ariaLabel="Group by"
          />
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {GROUPS.find((g) => g.value === group)?.hint}
          </Typography>
        </Box>

        {/* --- The headline figures --- */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 3, mb: 5 }}>
          <Figure label="Area validated" value={ha(totals?.area_ha ?? 0, 0)} emphasis loading={isLoading} />
          <Figure label="Polygons" value={num(totals?.plots ?? 0)} loading={isLoading} />
          <Figure label="Runs completed" value={num(totals?.runs ?? 0)} loading={isLoading} />
          <Figure
            label={scope === 'platform' ? 'Organisations active' : 'Uploads'}
            value={num(scope === 'platform' ? totals?.organisations ?? 0 : totals?.uploads ?? 0)}
            loading={isLoading}
          />
        </Box>

        <Surface sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, columnGap: 3, px: 5 }}>
            {COLUMNS.map((c) => (
              <Box
                key={c.key}
                sx={{
                  display: 'flex', alignItems: 'center', height: 36,
                  justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
                  font: `500 11px ${tokens.font.sans}`, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'text.disabled',
                }}
              >
                {c.label}
              </Box>
            ))}
            {/* The first load only. A background refetch must not blank the
                table, and must not be able to hide an empty result either. */}
            {isLoading && (
              <SkeletonGridRows
                columns={COLUMNS.length}
                rows={5}
                height={44}
                align={COLUMNS.map((c, i) => ('align' in c ? i : -1)).filter((i) => i >= 0)}
              />
            )}
            {rows.map((row) => (
              <Row key={String(row.group)} row={row} group={group} />
            ))}
          </Box>

          {!isLoading && rows.length === 0 && (
            <EmptyState
              title="Nothing used yet"
              body="Upload plots into a region or run the checks and it will appear here."
            />
          )}
        </Surface>
        </>)}
      </Box>
    </AppShell>
  )
}

function Row({ row, group }: { row: UsageRow; group: Group }) {
  const cell = {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    borderTop: `1px solid ${tokens.color.ink200}`,
    minWidth: 0,
  }
  const right = (node: React.ReactNode) => (
    <Box sx={{ ...cell, justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>{node}</Box>
  )

  return (
    <Box sx={{ display: 'contents', '&:hover > *': { backgroundColor: tokens.color.ink50 } }}>
      <Box sx={{ ...cell, minWidth: 0 }}>
        <Label value={row.group} label={row.label} group={group} />
      </Box>
      {right(<Typography variant="body2" sx={{ color: 'text.secondary' }}>{num(row.events)}</Typography>)}
      {right(<Typography variant="body2">{num(row.uploads)}</Typography>)}
      {right(<Typography variant="body2">{num(row.runs)}</Typography>)}
      {right(<Typography variant="body2">{num(row.plots)}</Typography>)}
      {right(
        <Typography sx={{ font: `500 13px ${tokens.font.sans}` }}>{ha(row.area_ha, 0)}</Typography>,
      )}
      {right(
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {row.last_seen ? formatDate(row.last_seen) : '—'}
        </Typography>,
      )}
    </Box>
  )
}

/**
 * A uuid is what a user is; showing all 36 characters of one in a table
 * teaches nobody anything. The first eight identify it well enough to match
 * against a partner's own records, and the title attribute carries the rest.
 * Where the API could supply a name — an organisation, a key — it does, and
 * the uuid drops to a subtitle.
 */
function Label(
  { value, label, group }: { value: unknown; label?: string | null; group: Group },
) {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
        {group === 'key' ? 'Not from a key' : 'Unattributed'}
      </Typography>
    )
  }
  const text = String(value)
  if (group === 'day' || group === 'kind') {
    return <Typography variant="body2">{text}</Typography>
  }
  if (label) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>{label}</Typography>
        <Mono sx={{ fontSize: 11, color: 'text.disabled' }} title={text}>{text.slice(0, 8)}</Mono>
      </Box>
    )
  }
  return (
    <Mono sx={{ fontSize: 13 }} title={text}>
      {text.slice(0, 8)}
    </Mono>
  )
}

function Figure({
  label, value, emphasis, loading,
}: { label: string; value: string; emphasis?: boolean; loading?: boolean }) {
  return (
    <Surface sx={{ px: 5, py: 4 }}>
      {/* A zero that turns into 4,912 a moment later was read as the real
          figure. A block cannot be misread as a number. */}
      {loading ? (
        <Box sx={{ height: emphasis ? 33 : 26, display: 'flex', alignItems: 'center' }}>
          <Skeleton width={emphasis ? 120 : 84} height={emphasis ? 24 : 20} />
        </Box>
      ) : (
      <Typography
        sx={{
          font: `${emphasis ? 700 : 500} ${emphasis ? 30 : 24}px ${
            emphasis ? tokens.font.display : tokens.font.sans
          }`,
          color: emphasis ? tokens.color.brandDeep : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      )}
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
        {label}
      </Typography>
    </Surface>
  )
}
